/**
 * Signal response record — the amendment record's signal half (SIG-3).
 *
 * The moment it catches (02B §5): "The plan changed while I was heads-down —
 * what changed, and why, in 90 seconds."
 *
 * Renders the responses on the record: which signal, what response, which
 * Decision, what we said we'd expect, and when we said we'd check. A reader
 * who wasn't in the room can act on it, defend it, and recognize when it no
 * longer applies — without finding the person who made it.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getSignalOccurrence } from '../../types/signal-events';
import {
  listSignalResponseRecord,
  type SignalResponseRecordEntry,
} from '../../lib/signalEventsService';
import { formatCalendarDate } from '../../lib/calendarDate';

const KIND_COPY: Record<string, string> = {
  change: 'Changed course on',
  hold: 'Held course against',
  monitor: 'Watching',
  dismiss: 'Dismissed',
};

function fmtDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function SignalResponseRecord({
  workspaceId,
  porId,
  excludeSignalIds,
}: {
  workspaceId: string;
  porId?: string;
  /** Signals whose answer already renders inline on their own row (SignalLog). */
  excludeSignalIds?: string[];
}) {
  const [entries, setEntries] = useState<SignalResponseRecordEntry[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listSignalResponseRecord({ workspaceId, porId })
      .then((rows) => {
        if (!cancelled) setEntries(rows);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setLoadError(e instanceof Error ? e.message : 'Could not load the record.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId, porId]);

  if (loadError) {
    return <p className="text-xs text-luminous-coral-700 mt-3">{loadError}</p>;
  }
  const excluded = new Set(excludeSignalIds ?? []);
  const shown = entries.filter((e) => !excluded.has(e.signalEventId));
  if (shown.length === 0) return null;

  return (
    <div className="mt-4">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-2">
        On the record
      </h4>
      <ul className="space-y-2">
        {shown.map((e) => {
          const occurrence = getSignalOccurrence(e.occurrence)?.label ?? e.occurrence;
          return (
            <li
              key={`${e.signalEventId}-${e.decisionId}`}
              className="border border-[var(--rule)] dark:border-[var(--rule)] px-3 py-2.5"
            >
              <p className="text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                <span className="font-medium">{KIND_COPY[e.responseKind] ?? e.responseKind}</span>{' '}
                “{occurrence}”
              </p>
              <p className="text-[11px] font-mono text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">
                {fmtDate(e.linkedAt)} ·{' '}
                <Link
                  to={`/prioritization/decisions/${e.decisionId}`}
                  className="text-primary-700 hover:underline dark:text-[var(--text-sec)]"
                >
                  {e.decisionTitle}
                </Link>
                {e.reviewByDate && ` · check by ${formatCalendarDate(e.reviewByDate)}`}
              </p>
              {e.reconsiderationConditions && (
                <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-1.5 whitespace-pre-line line-clamp-4">
                  {e.reconsiderationConditions}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
